/**
 * Door swing and passage clearance collision checks.
 * Uses accurate arc polygon for door swing zones.
 *
 * Accepts standard room data format (wall.alignment.bottomLeft/bottomRight)
 * and normalizes to the geometry needed for collision detection.
 */

const { doPolygonsOverlap, getObjectCorners } = require('./geometry');
const { getWallInwardNormal } = require('../vanity-assembly/placementUtils');
const { apply3DRotationTo2D } = require('../geometry/rotationUtils');

// Door hinge resolution (matches bathroom-analyzer/pipeline/5.6-doorBlocking.js)
const HINGE_LEFT_ON_PRIORITY = {
  leftInswing: false,
  rightInswing: true,
  leftOutswing: true,
  rightOutswing: false,
};

// Sign of the quarter turn the leaf makes, in the 2D (x, z) frame the
// floor-plan canvas draws in. Mirrors SWING_DOOR_CONFIG.swingAngle in
// web-app/src/constants/doorTypes.js + functions/shared/doorTypes.js;
// `doorJambFrame.ci.test.js` fails if the three drift apart.
const SWING_ANGLE = {
  leftInswing: Math.PI / 2,
  rightInswing: Math.PI / 2,
  leftOutswing: -Math.PI / 2,
  rightOutswing: -Math.PI / 2,
};

/**
 * Resolve the hinge jamb + swing angle for a swing door. A faithful port of
 * `getSwingDoorConfig()` in web-app/src/constants/doorTypes.js.
 *
 * `hingeAtStart` names a jamb in the DOOR's own direction frame — see
 * `doorJambAxis()` for why that distinction is load-bearing.
 *
 * @returns {{hingeAtStart: boolean, swingAngle: number}|null} null for
 *   non-swing or unknown door types.
 */
function resolveSwingConfig(doorType, wallFacesPriorityRoom) {
  const hingeLeft = HINGE_LEFT_ON_PRIORITY[doorType];
  if (hingeLeft == null) return null;
  const base = SWING_ANGLE[doorType];
  if (wallFacesPriorityRoom == null) {
    return { hingeAtStart: hingeLeft, swingAngle: base };
  }
  if (wallFacesPriorityRoom) {
    return { hingeAtStart: hingeLeft, swingAngle: hingeLeft ? base : -base };
  }
  const hingeAtStart = !hingeLeft;
  return { hingeAtStart, swingAngle: hingeAtStart ? -base : base };
}

function resolveHingeAtStart(doorType, wallFacesPriorityRoom) {
  const cfg = resolveSwingConfig(doorType, wallFacesPriorityRoom);
  return cfg ? cfg.hingeAtStart : null;
}

/** The door's own doorType, whether it arrives room-shaped or analyzer-shaped. */
function doorTypeOf(door) {
  return door?.category?.door?.doorType ?? door?.doorType ?? null;
}

/**
 * The wall axis, oriented the way the DOOR runs.
 *
 * `hingeAtStart` names a jamb in the door's own direction frame — the frame
 * every renderer resolves it in, since `computeSwingDoorGeometry` and the 2D
 * canvas both take their direction from `door.rotation`. A room wall's
 * `bottomLeft -> bottomRight` runs the OTHER way for ~a third of scanned
 * doors (739 of 2257 swing-door room copies on dev), because
 * `ensureInwardFacingCorners` picks a room's winding per room, not per door.
 * Resolving the hinge against the wall alone therefore puts the arc on the
 * wrong jamb — and flips which side of the wall the leaf opens toward.
 *
 * Only the SIGN comes from the door; the axis stays the wall's, so the jambs
 * still land exactly on the wall line even when the door sits a fraction of a
 * degree off it.
 *
 * @returns {{x: number, z: number}} wallDir, possibly negated.
 */
function doorJambAxis(door, wallDir) {
  if (!door?.rotation) return wallDir;
  const [dx, dz] = apply3DRotationTo2D(door.rotation);
  return dx * wallDir.x + dz * wallDir.z < 0
    ? { x: -wallDir.x, z: -wallDir.z }
    : wallDir;
}

/**
 * Which way the LEAF sweeps: an outswing door's arc is on the far side of
 * the wall, so it never lands on a room fixture. The zone still has to be
 * built (the threshold guard below is real either way) — only the arc's
 * normal flips.
 *
 * Modelling every door as inswing is what let `vanity-yields-to-door`
 * amputate a vanity for a door `door-swing-yields` had ALREADY re-swung
 * outward to spare that same vanity: phase 2 flips the door, phase 4 re-
 * fights the fight against an arc that no longer exists.
 */
function isOutswing(doorType) {
  return String(doorType || '')
    .toLowerCase()
    .includes('outswing');
}

/**
 * Get wall direction and normal from standard room data wall format.
 */
function getWallVectors(wall) {
  const start = wall.alignment?.bottomLeft || wall.start;
  const end = wall.alignment?.bottomRight || wall.end;
  if (!start || !end) return null;

  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const length = Math.sqrt(dx * dx + dz * dz);
  if (length === 0) return null;

  const wallDir = { x: dx / length, z: dz / length };
  const normal = wall.normal || getWallInwardNormal(wall);

  return { start, end, wallDir, normal, length };
}

/**
 * Seat a door on its parent wall: the jamb pair, in the DOOR's direction
 * frame, plus the wall vectors both the swing zone and the open-leaf test
 * need. One place so the two can never disagree about where a jamb is.
 *
 * @returns {{start, end, wallDir, normal, length, width, axis, center,
 *   startJamb, endJamb}|null}
 */
function seatDoorOnWall(door, walls) {
  const wall = walls.find((w) => w.id === door.parentWallIds?.[0]);
  if (!wall) return null;

  const wv = getWallVectors(wall);
  if (!wv) return null;

  const width = door.width || door.dimensions?.x || 0.8;
  const axis = doorJambAxis(door, wv.wallDir);
  const halfWidth = width / 2;

  // Project door center onto wall
  const dx = door.center.x - wv.start.x;
  const dz = door.center.z - wv.start.z;
  const t = dx * wv.wallDir.x + dz * wv.wallDir.z;
  const center = {
    x: wv.start.x + wv.wallDir.x * t,
    z: wv.start.z + wv.wallDir.z * t,
  };

  return {
    ...wv,
    width,
    axis,
    center,
    startJamb: {
      x: center.x - axis.x * halfWidth,
      z: center.z - axis.z * halfWidth,
    },
    endJamb: {
      x: center.x + axis.x * halfWidth,
      z: center.z + axis.z * halfWidth,
    },
  };
}

/**
 * Where the leaf comes to rest when the door is fully open, in world (x, z).
 *
 * The swept quarter-arc is the SAME region for either handing — it always
 * spans jamb to jamb — so an arc test cannot tell two handings apart. The
 * open leaf can: one handing folds flat against the perpendicular wall, the
 * other plants the leaf across the middle of the room.
 *
 * Needs `door.rotation`: without it the door's direction frame is unknown,
 * and both the hinge jamb and the side the leaf opens toward are coin flips
 * (see `doorJambAxis`). Returns null rather than guess.
 *
 * @returns {{hinge, tip, openAngle, normal, width}|null}
 */
function computeOpenLeaf(door, walls) {
  if (!door?.rotation) return null;
  const cfg = resolveSwingConfig(doorTypeOf(door), door.wallFacesPriorityRoom);
  if (!cfg) return null;

  const seat = seatDoorOnWall(door, walls);
  if (!seat) return null;

  const hinge = cfg.hingeAtStart ? seat.startJamb : seat.endJamb;
  const opposite = cfg.hingeAtStart ? seat.endJamb : seat.startJamb;
  const closedAngle = Math.atan2(opposite.z - hinge.z, opposite.x - hinge.x);
  const openAngle = closedAngle + cfg.swingAngle;

  return {
    hinge,
    openAngle,
    normal: seat.normal,
    width: seat.width,
    tip: {
      x: hinge.x + Math.cos(openAngle) * seat.width,
      z: hinge.z + Math.sin(openAngle) * seat.width,
    },
  };
}

/**
 * Does this door's leaf sweep INTO the given room?
 *
 * `isOutswing(doorType)` cannot answer this: "outswing" is relative to the
 * door's PRIORITY room, so for the room on the other side of the wall it
 * means the opposite. This is measured off the leaf itself, so it is right
 * from either side.
 *
 * @returns {boolean|null} null when undeterminable (no rotation, non-swing
 *   type, unresolvable wall, or a leaf that lands along the wall line).
 */
function doorSwingsIntoRoom(door, walls) {
  const leaf = computeOpenLeaf(door, walls);
  if (!leaf) return null;
  const d =
    (leaf.tip.x - leaf.hinge.x) * leaf.normal.x +
    (leaf.tip.z - leaf.hinge.z) * leaf.normal.z;
  if (Math.abs(d) < 1e-6) return null;
  return d > 0;
}

// A door leaf is a nominal 1-3/4" panel; 2" keeps the footprint slightly
// conservative without letting it read as a wall.
const OPEN_LEAF_THICKNESS = 0.0508;

/**
 * The open leaf as a thin rectangle, for overlap tests against fixtures.
 *
 * @returns {Array<{x, z}>|null} 4 corners, or null if the leaf is unresolvable
 */
function openLeafFootprint(door, walls) {
  const leaf = computeOpenLeaf(door, walls);
  if (!leaf) return null;
  const dx = leaf.tip.x - leaf.hinge.x;
  const dz = leaf.tip.z - leaf.hinge.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return null;
  const half = OPEN_LEAF_THICKNESS / 2;
  const px = (-dz / len) * half;
  const pz = (dx / len) * half;
  return [
    { x: leaf.hinge.x + px, z: leaf.hinge.z + pz },
    { x: leaf.tip.x + px, z: leaf.tip.z + pz },
    { x: leaf.tip.x - px, z: leaf.tip.z - pz },
    { x: leaf.hinge.x - px, z: leaf.hinge.z - pz },
  ];
}

/**
 * Calculate door swing zone polygon(s).
 * Returns an array of sub-polygons (arc + threshold guard) that must remain
 * clear. The arc is the LEAF's sweep — on the room side for an inswing
 * door, on the far side of the wall for an outswing one. The threshold
 * guard is the strip you walk through, and is always on the room side.
 *
 * @param {Object} door - Door with center, width/dimensions, parentWallIds, category.door.doorType
 * @param {Object[]} walls - Room walls
 * @param {Object} [options]
 * @param {boolean} [options.includeThresholdGuard=true] - keep the passage
 *   strip in the returned zones. Pass `false` when the caller is asking
 *   only "does this obstruct the DOOR LEAF": the guard extends 2" past
 *   each jamb, so any cabinet on the perpendicular wall running up to the
 *   jamb clips it, and a caller that shortens cabinetry will amputate a
 *   run over a corner that was never in the doorway. Passage clearance has
 *   its own rules (`vanity-yields-to-clearance`, `entranceClearance`) with
 *   proportionate caps.
 * @returns {Object[][]|null} Array of sub-polygon corner arrays
 */
function calculateDoorZone(door, walls, options = {}) {
  const { includeThresholdGuard = true } = options;
  const seat = seatDoorOnWall(door, walls);
  if (!seat) return null;

  const doorWidth = seat.width;
  const radius = doorWidth + 0.0254; // doorWidth + 1"
  const GUARD_EXTRA = 0.0508; // 2" past each side
  const GUARD_DEPTH = 0.1524; // 6" into room
  const ARC_SEGMENTS = 8;

  const { wallDir, normal, axis, center } = seat;
  const halfWidth = doorWidth / 2;
  // The leaf sweeps into the room for an inswing door and away from it for
  // an outswing one. The threshold guard stays on the room side regardless
  // — you walk through the opening whichever way the leaf goes.
  //
  // Measured off the leaf where the geometry allows it: `isOutswing` reads
  // the doorType, which is named for the door's PRIORITY room, so it has the
  // sign backwards for the room on the other side of the wall.
  const swingsIn = doorSwingsIntoRoom(door, walls);
  const outswing = swingsIn == null ? isOutswing(doorTypeOf(door)) : !swingsIn;
  const arcNormal = outswing ? { x: -normal.x, z: -normal.z } : normal;

  const leftEnd = seat.startJamb;
  const rightEnd = seat.endJamb;

  function makeArc(hinge, startDir) {
    const points = [{ x: hinge.x, z: hinge.z }];
    for (let i = 0; i <= ARC_SEGMENTS; i++) {
      const theta = (Math.PI / 2) * (i / ARC_SEGMENTS);
      points.push({
        x:
          hinge.x +
          radius *
            (startDir.x * Math.cos(theta) + arcNormal.x * Math.sin(theta)),
        z:
          hinge.z +
          radius *
            (startDir.z * Math.cos(theta) + arcNormal.z * Math.sin(theta)),
      });
    }
    return points;
  }

  const guardHalf = halfWidth + GUARD_EXTRA;
  const sideGuard = [
    {
      x: center.x - wallDir.x * guardHalf,
      z: center.z - wallDir.z * guardHalf,
    },
    {
      x: center.x + wallDir.x * guardHalf,
      z: center.z + wallDir.z * guardHalf,
    },
    {
      x: center.x + wallDir.x * guardHalf + normal.x * GUARD_DEPTH,
      z: center.z + wallDir.z * guardHalf + normal.z * GUARD_DEPTH,
    },
    {
      x: center.x - wallDir.x * guardHalf + normal.x * GUARD_DEPTH,
      z: center.z - wallDir.z * guardHalf + normal.z * GUARD_DEPTH,
    },
  ];

  // Resolve hinge side. `hingeAtStart` is in the door's frame, so the jambs
  // it selects must be too — hence `axis`, not `wallDir`.
  const hingeAtStart = resolveHingeAtStart(
    doorTypeOf(door),
    door.wallFacesPriorityRoom
  );

  const guard = includeThresholdGuard ? [sideGuard] : [];

  if (hingeAtStart === true) {
    return [makeArc(leftEnd, axis), ...guard];
  } else if (hingeAtStart === false) {
    return [makeArc(rightEnd, { x: -axis.x, z: -axis.z }), ...guard];
  }

  // Unknown hinge — check both arcs
  return [
    makeArc(leftEnd, axis),
    makeArc(rightEnd, { x: -axis.x, z: -axis.z }),
    ...guard,
  ];
}

/**
 * Resolve the canonical source for a door's opening-itself fields
 * (doorType, wallFacesPriorityRoom) and coords (center, dimensions).
 * Post-P3a those live only on the structure copy; pre-P3a room copies
 * still carry them. When a structure-side door is reachable via the
 * room copy's `structureOpeningId`, prefer it; otherwise fall back to
 * the room copy (today's behavior). Keeps the room-side `parentWallIds`
 * so wall-relative geometry still resolves against the room walls.
 */
function resolveDoorSource(door, structureDoorsById) {
  const structureDoor =
    structureDoorsById?.get?.(door.structureOpeningId) || null;
  if (!structureDoor) return door;
  return {
    ...door,
    center: structureDoor.center,
    // Room copies are slim post-P3a; without rotation the door has no
    // direction frame and `computeOpenLeaf` has to decline.
    rotation: structureDoor.rotation ?? door.rotation,
    dimensions: structureDoor.dimensions,
    width: structureDoor.dimensions?.x ?? door.width,
    category: structureDoor.category || door.category,
    wallFacesPriorityRoom:
      structureDoor.wallFacesPriorityRoom != null
        ? structureDoor.wallFacesPriorityRoom
        : door.wallFacesPriorityRoom,
  };
}

/**
 * Check if an object's physical bounds overlap any door swing zone.
 *
 * @param {Object} object - The fixture being placed
 * @param {Object} roomData - { doors, walls }
 * @param {Map<string, Object>} [structureDoorsById] - Optional structure-
 *   door lookup. When provided each room-copy door is resolved through
 *   it so coords / doorType / wallFacesPriorityRoom come from the
 *   canonical structure-level source (P3a-ready).
 * @returns {Array<{type, target, reason}>} Collisions found
 */
function checkDoorSwingCollisions(object, roomData, structureDoorsById) {
  const collisions = [];
  const doors = roomData.doors || [];
  const walls = roomData.walls || [];

  if (!object.center || !object.dimensions) return collisions;

  const objCorners = getObjectCorners(object);

  for (const rawDoor of doors) {
    const door = resolveDoorSource(rawDoor, structureDoorsById);
    // Early-out: a leaf that sweeps away from the room can only ever match
    // its threshold guard. `calculateDoorZone` now models that itself — this
    // stays as a cheap skip, not as the only thing enforcing it. Measured
    // off the leaf when we can, since "inswing" names the PRIORITY room.
    const swingsIn = doorSwingsIntoRoom(door, walls);
    if (swingsIn === false) continue;
    if (swingsIn == null) {
      const doorType = door.category?.door?.doorType || '';
      if (!doorType.toLowerCase().includes('inswing')) continue;
    }

    const doorZones = calculateDoorZone(door, walls);
    if (!doorZones) continue;

    for (const zone of doorZones) {
      if (doPolygonsOverlap(objCorners, zone)) {
        collisions.push({
          type: 'door-swing',
          target: { id: rawDoor.id, type: 'door' },
          reason: `Blocks door swing of ${rawDoor.id}`,
        });
        break; // One collision per door is enough
      }
    }
  }

  return collisions;
}

module.exports = {
  calculateDoorZone,
  checkDoorSwingCollisions,
  computeOpenLeaf,
  doorJambAxis,
  doorSwingsIntoRoom,
  openLeafFootprint,
  resolveSwingConfig,
  HINGE_LEFT_ON_PRIORITY,
  SWING_ANGLE,
  OPEN_LEAF_THICKNESS,
};
